import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import PersonalInformationFormFields from '@/features/onboarding/personal-information/components/fields';
import { useIsPersonalInformationFieldLocked } from '@/features/onboarding/personal-information/hooks/use-is-field-locked';
import type { PersonalInformation, PersonalInformationCountryOfResidency } from '@/features/onboarding/personal-information/schema';
import { mainstreamCountriesQueryOptions, useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import { queryClient } from '@/lib/query-client';
import { isJurisdictionPermitted } from '@/utils';

export const PersonalInformationFormFieldsetCountryOfResidencyLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('personalInformation.countryOfResidencyTitle')}</PageHeadingTitle>
        <PageHeadingDescription>{t('personalInformation.countryOfResidencyDescription')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
PersonalInformationFormFieldsetCountryOfResidencyLegend.displayName = 'PersonalInformationFormFieldsetCountryOfResidencyLegend';

export const PersonalInformationFormFieldsetCountryOfResidency = () => {
  const form = useFormContext<PersonalInformationCountryOfResidency>();
  const { defaultValues } = form.formState;
  const isLocked = useIsPersonalInformationFieldLocked();
  const { countriesAsOptions } = useMainstreamCountries();
  const { t } = useTranslation();

  return (
    <PersonalInformationFormFields>
      <FormField
        control={form.control}
        name="countryOfResidency"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('personalInformation.countryOfResidency')}*</FormLabel>
            <PopoverControlled>
              {(_, setOpen) => (
                <>
                  <PopoverTrigger disabled={isLocked('countryOfResidency')} asChild>
                    <FormControl>
                      <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                        <PseudoInputLabel>
                          {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('personalInformation.placeholders.countryOfResidency')}
                        </PseudoInputLabel>
                        <PseudoInputIcon>
                          <ChevronsUpDown />
                        </PseudoInputIcon>
                      </PseudoInputButton>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" stretch>
                    <PseudoInputOptions
                      empty={t('statuses.noResults')}
                      options={countriesAsOptions}
                      placeholder={t('fundamentals.searchCountries')}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(field.value === value ? defaultValues?.countryOfResidency : value);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </>
              )}
            </PopoverControlled>
            <FormMessage />
          </FormItem>
        )}
      />
    </PersonalInformationFormFields>
  );
};
PersonalInformationFormFieldsetCountryOfResidency.displayName = 'PersonalInformationFormFieldsetCountryOfResidency';

const beforeStepLeave: FormStepGuard<PersonalInformation> = async ({ form }, next) => {
  if (!form) return next(false);

  const countries = await queryClient.ensureQueryData(mainstreamCountriesQueryOptions);
  const countryOfResidency = form.watch('countryOfResidency');

  if ((await form.trigger()) && !isJurisdictionPermitted(countryOfResidency, countries ?? {})) {
    // Relative, as this step also runs under a periodic review's path
    return next({
      to: `forbidden-jurisdiction/${countryOfResidency}`,
      replace: true,
    });
  }

  return next();
};

PersonalInformationFormFieldsetCountryOfResidency.beforeStepLeave = beforeStepLeave;
